import Grid from '@mui/material/Grid';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { LOCAL_STORAGE_KEYS, errorMessage } from 'constants/CommonConstants';
import { EMAIL_REGEX, PHONE_REGEX } from 'constants/Regex';
import { formPatternRules, formRequiredRules } from 'helper';
import useQueryParams from 'hooks/UseQueryParams';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CommonInput } from 'sharedComponents/inputGroup';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { postLeadCreation } from 'store/LeadCreationState';
import { RootState } from 'store/store';
import { Lead } from '../LeadGenInterface';
import { getIpv4Data } from 'store/CommonModuleState';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';

const PersonalInfo: React.ForwardRefRenderFunction<unknown, StepperFromProps> = (
  { onNext, isSubmit, mappedData },
  ref
) => {
  // Hooks
  const { t } = useTranslation();
  const queryParams = useQueryParams();

  const hasCalledNextStep = React.useRef(false);

  //Query Params
  const leadSourceId = queryParams.get('lead_source'); // 655c90ba263df100130afb23
  const gclickid = queryParams.get('Utm_ClickId') ?? '';
  const utm_source = queryParams.get('utm_source') ?? '';
  const utm_ad = queryParams.get('utm_ad') ?? '';
  const utm_adset = queryParams.get('utm_adset') ?? '';
  const utm_campaign = queryParams.get('utm_campaign') ?? '';

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};

  // React Hook Form
  const { control, trigger, getValues } = useFormContext();

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    // const IPv4 = await getIpv4Data();

    let IPv4: string = '';

    try {
      // Create a timeout promise that rejects after a certain time
      const timeout = new Promise(
        (_, reject) => setTimeout(() => reject(new Error('Request timed out')), 2000) // 3 seconds timeout
      );

      // Attempt to fetch IPv4 address with timeout
      IPv4 = await Promise.race([getIpv4Data(), timeout]);
    } catch (error) {
      console.error('Error fetching IPv4 data:', error?.message);
      // Handle the error as needed (e.g., continue with default IPv4)
    }

    const {
      firstName,
      lastName,
      email,
      phone,
      leadgen_address_country,
      leadgen_address_postal,
      leadgen_address_lookupStreetName,
      leadgen_address_lookupStreetNumber,
      leadgen_address_city,
      leadgen_address_province,
    } = getValues();

    const personalInfoData: Lead = {
      firstName,
      lastName,
      email,
      phone,
      leadSourceId: widgetData?.leadSourceId,
      isSubmit,
      country: leadgen_address_country,
      zipCode: widgetData?.zipCode || leadgen_address_postal,
      originalZipCode: leadgen_address_postal,
      streetName: leadgen_address_lookupStreetName,
      addressNumber: leadgen_address_lookupStreetNumber,
      city: leadgen_address_city,
      province: leadgen_address_province,
      leadDetails: {
        'IP Address': IPv4,
        'Utm ClickID': gclickid,
        utm_source: utm_source,
        utm_ad: utm_ad,
        utm_adset: utm_adset,
        utm_campaign: utm_campaign,
        'Widget Name': authenticateClientData?.data?.widgetName,
        'Widget ID': authenticateClientData?.data?.widgetId,
        ...mappedData,
      },
      adfDistributionList: widgetData?.adfDistributionList?.join(',') || '',
    };

    dispatch(postLeadCreation({ data: personalInfoData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      console.log('Personal Info Step Response:', res);
      if (
        res?.payload?.success &&
        (res?.payload?.data?.draftLeadId || res?.payload?.data?._id || res?.payload?.data?.Lead?.leadId)
      ) {
        res?.payload?.data?.draftLeadId &&
          localStorage.setItem(LOCAL_STORAGE_KEYS.draftLeadId, res?.payload?.data?.draftLeadId);
        onNext?.();
      }
    });
  };

  useImperativeHandle(ref, () => ({
    nextStep,
  }));

  return (
    <>
      <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.personalInfoTxt1')}</h3>
      <Grid container rowSpacing={3} columnSpacing={2}>
        <Grid item sm={6} xs={12}>
          <CommonInput
            name='firstName'
            control={control}
            label={t('stepperForm.firstName')}
            placeholder={t('stepperForm.enterFirstName')}
            triggerValidationForField={trigger}
            rules={formRequiredRules(errorMessage(t).firstNameRequired)}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <CommonInput
            name='lastName'
            control={control}
            label={t('stepperForm.lastName')}
            placeholder={t('stepperForm.enterLastName')}
            triggerValidationForField={trigger}
            rules={formRequiredRules(errorMessage(t).lastNameRequired)}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <CommonInput
            name='email'
            control={control}
            label={t('stepperForm.emailEg')}
            placeholder={t('stepperForm.enterEmail')}
            triggerValidationForField={trigger}
            rules={{
              ...formRequiredRules(errorMessage(t).emailRequired),
              ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
            }}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <CommonPhoneInput
            name='phone'
            control={control}
            label={t('stepperForm.phone')}
            placeholder={t('stepperForm.enterPhone')}
            triggerValidationForField={trigger}
            rules={{
              ...formRequiredRules(errorMessage(t).phoneRequired),
              ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

const PersonalInfoStep = React.forwardRef(PersonalInfo);

export default PersonalInfoStep;
